import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { OptiBaySessionInterpretation } from '@/types/optibay';

const sb = supabase as any;

export function useSessionInterpretations(sessionId: string | undefined) {
  return useQuery({
    queryKey: ['optibay-session-interpretations', sessionId],
    queryFn: async (): Promise<OptiBaySessionInterpretation[]> => {
      if (!sessionId) return [];
      const { data, error } = await sb
        .from('optibay_session_interpretations')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as OptiBaySessionInterpretation[];
    },
    enabled: !!sessionId,
  });
}

export interface AddInterpretationParams {
  sessionId: string;
  sessionTestPathId?: string;
  interpretationText: string;
  confidence?: string;
  rootCauseCategory?: string;
}

export function useAddInterpretation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddInterpretationParams) => {
      const { data, error } = await sb
        .from('optibay_session_interpretations')
        .insert({
          session_id: params.sessionId,
          session_test_path_id: params.sessionTestPathId || null,
          interpretation_text: params.interpretationText,
          confidence: params.confidence || 'medium',
          root_cause_category: params.rootCauseCategory || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionInterpretation;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-interpretations', data.session_id] });
    },
  });
}

export function useUpdateInterpretation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: {
      id: string;
      updates: Partial<Pick<OptiBaySessionInterpretation, 'interpretation_text' | 'confidence' | 'root_cause_category'>>;
    }) => {
      const { data, error } = await sb
        .from('optibay_session_interpretations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as OptiBaySessionInterpretation;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-interpretations', data.session_id] });
    },
  });
}

export function useRemoveInterpretation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, sessionId }: { id: string; sessionId: string }) => {
      const { error } = await sb
        .from('optibay_session_interpretations')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { sessionId };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['optibay-session-interpretations', result.sessionId] });
    },
  });
}
